import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import {
  BenchmarkProject,
  BenchmarkReportSummary,
} from 'spw-redux/modules/benchmark-reports/types';
import { colors } from 'spw-theme';
import { Box, Grid } from '@mui/material';
import { asUsf } from 'spw-shared/utils';
import useUnitConversion from 'spw-components/unit-conversion/hooks';

const Container = styled.div`
  .value {
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    margin-bottom: 4px;
  }
  .label {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    text-transform: uppercase;
  }
  .caveat {
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    text-align: center;
  }
  && > *:focus {
    outline: none;
  }
`;

type Props = {
  benchmarkProjects: BenchmarkProject[];
  summary: BenchmarkReportSummary;
};

const STATES_OUTSIDE_CONTIGUOUS_USA = ['HI', 'AK'];

function BenchmarkReportOverviewCard({ benchmarkProjects, summary }: Props) {
  const { getUnit, convertArea } = useUnitConversion();
  const {
    linkedUSAStatesMap,
    linkedProjectsCount,
    thereAreLocationsOutsideUSA,
  } = useMemo(() => {
    const map: { [stateCode: string]: boolean } = {};
    let linkedCount = 0;
    let locationsOutsideUSA = false;
    if (benchmarkProjects)
      benchmarkProjects.forEach((brProject) => {
        const { countryCode, state } = brProject.project;
        if (
          countryCode &&
          state &&
          brProject.linked &&
          (countryCode !== 'USA' ||
            (countryCode === 'USA' &&
              STATES_OUTSIDE_CONTIGUOUS_USA.includes(state)))
        )
          locationsOutsideUSA = true;
        if (brProject.linked) linkedCount += 1;
        const stateCode =
          brProject.project.countryCode === 'USA' && brProject.linked
            ? brProject.project.state
            : '';
        map[stateCode] = true;
      });
    return {
      linkedProjectsCount: linkedCount,
      linkedUSAStatesMap: map,
      thereAreLocationsOutsideUSA: locationsOutsideUSA,
    };
  }, [benchmarkProjects]);

  return (
    <Container>
      <div className="title">Report Overview</div>
      <Grid container columnSpacing={4} mb={2.5} justifyContent="center">
        <Grid item xs={6} height={200} boxSizing="border-box">
          <Box mt={-4}>
            <ComposableMap projection="geoAlbersUsa">
              <Geographies geography="/us-states-map.json">
                {({ geographies }) => {
                  return (
                    <>
                      {geographies.map((geography) => (
                        <Geography
                          key={geography.properties?.name}
                          data-testid={`us_state_${geography.properties?.code}`}
                          geography={geography}
                          strokeWidth={1}
                          stroke="#fff"
                          style={{
                            default: { outline: 'none' },
                            hover: { outline: 'none' },
                            pressed: { outline: 'none' },
                          }}
                          fill={
                            linkedUSAStatesMap[geography.properties?.code]
                              ? colors.mainGray
                              : colors.grayMedium
                          }
                        />
                      ))}
                    </>
                  );
                }}
              </Geographies>
            </ComposableMap>
          </Box>
        </Grid>
        <Grid
          item
          xs={4}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          height={200}
          boxSizing="border-box"
        >
          <Box mb={2}>
            <div className="value" data-testid="report_overview_industries">
              {summary?.industries?.length === 1
                ? summary.industries[0]
                : `Multiple (${summary?.industries?.length || 0})`}
            </div>
            <div className="label">Industry</div>
          </Box>
          <Box mb={2}>
            <div
              className="value"
              data-testid="report_overview_avg_project_size"
            >
              {asUsf(convertArea(summary?.averageProjectSize) || 0)}{' '}
              {getUnit('area')}
            </div>
            <div className="label">Avg Project Size</div>
          </Box>
          <Box>
            <div
              className="value"
              data-testid="report_overview_linked_projects_count"
            >
              {linkedProjectsCount}
            </div>
            <div className="label">Linked Projects</div>
          </Box>
        </Grid>
      </Grid>
      {thereAreLocationsOutsideUSA ? (
        <div
          className="caveat"
          data-testid="report_overview_projects_outside_us_warning"
        >
          *One or more projects in the report are located outside of the
          contiguous U.S.
        </div>
      ) : null}
    </Container>
  );
}

export default BenchmarkReportOverviewCard;
